import type React from 'react';
import { memo, useMemo } from 'react';

import * as styles from './styles';
import { Divider } from '@cobliteam/ui/components';
import moment from 'moment-timezone';

import { MediaType } from '@cobliteam/common-types';

import { useCameraTrip, useVehicle, useVehicleGroup } from '@cobliteam/hooks';

import { CaptionDefault, Checkbox, DescriptionTitle, HintPopup, Label, MovieFilterIcon } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import useMediaStore from '../../../../store/zustand/useMediaStore';
import { DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY, MEDIA_REQUEST_MODAL_BASE_PERIOD, MEDIA_REQUEST_MODAL_TRIPS_LIMIT } from '../../../../utils/mediaUtils';

export const ConfirmationStep: React.FC = memo(() => {
  const mediaRequestData = useMediaStore((state) => state.requestMediaData);
  const setMediaRequestData = useMediaStore((state) => state.setRequestMediaData);

  const { getVehicles } = useVehicle();
  const { data: vehicles = [] } = getVehicles({
    enabled: true,
    staleTime: 300000,
  });

  const vehicle = vehicles.find((vehi) => vehi.id === mediaRequestData?.vehicleId);

  const { getCameraTrip } = useCameraTrip();
  const { data: tripsPagination } = getCameraTrip(
    mediaRequestData!.vehicleId!,
    MEDIA_REQUEST_MODAL_BASE_PERIOD.before.valueOf(),
    MEDIA_REQUEST_MODAL_BASE_PERIOD.after.valueOf(),
    DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY,
    MEDIA_REQUEST_MODAL_TRIPS_LIMIT.min,
    MEDIA_REQUEST_MODAL_TRIPS_LIMIT.max,
    { column: 'startTime', order: 'desc' },
    {
      enabled: !!mediaRequestData?.vehicleId,
    }
  );

  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups = [] } = getVehicleGroups({
    staleTime: 300000,
  });

  const trip = useMemo(
    () => tripsPagination?.content?.find((t) => t.startTimestamp === mediaRequestData?.requestPeriodStart),

    [JSON.stringify(tripsPagination?.content), mediaRequestData?.requestPeriodStart]
  );
  const mediaTypeLabel = mediaRequestData?.mediaType === MediaType.FOOTAGE_VIDEO ? 'Vídeo de um trecho' : 'Resumo do trajeto';
  const mediaType = mediaRequestData?.mediaType === MediaType.FOOTAGE_VIDEO ? '(até 1 minuto)' : '(time-lapse)';
  const endTimestamp = moment(trip?.endTimestamp);
  const startTimestamp = moment(trip?.startTimestamp);
  const requestedDay = moment(mediaRequestData?.requestDate).format('DD/MM/YYYY');

  const requestPeriod =
    mediaRequestData?.mediaType === MediaType.FOOTAGE_VIDEO ? mediaRequestData.requestMinute : startTimestamp.format('HH:mm').concat(' às ', endTimestamp.format('HH:mm'));

  const licensePlate = vehicle?.licensePlate ? vehicle.licensePlate : 'Sem placa';

  const modelName = vehicle?.model.name ? vehicle.model.name : 'Sem modelo';

  const deviceGroup = (() => {
    const group = vehicleGroups.find((g) => g.id === mediaRequestData?.vehicleGroupId);
    const groupName = group?.name || 'Sem grupo';

    return groupName.length > 13 ? (
      <HintPopup title={groupName} placement='top' arrow>
        <div>{`${groupName.slice(0, 13)}...`}</div>
      </HintPopup>
    ) : (
      groupName
    );
  })();

  const onClickEmailSendConfirmation = () => {
    setMediaRequestData({
      ...mediaRequestData,
      hasNotificationEnabled: !mediaRequestData?.hasNotificationEnabled,
    });
  };

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.leftSideWrapper}>
        <div className={styles.headerMargin}>
          <div className={styles.headerIconLine}>
            <Icon name='circle-check' />
            <DescriptionTitle>Confirmar solicitação</DescriptionTitle>
          </div>
          <CaptionDefault>
            Para que sua solicitação fique disponível, a mesma depende que o veículo esteja <b>ligado</b> e com <b>sinal de internet</b>.
          </CaptionDefault>
        </div>

        <Divider />

        <div className={styles.contentGroup}>
          <div className={styles.contentSection}>
            <div>
              <DescriptionTitle>Grupo e veículo</DescriptionTitle>
            </div>

            <div>
              <Icon name='cb-filters-vehicle-group-cb' className={styles.textSummaryIcon} />
              <Label>{deviceGroup}</Label>
            </div>

            <div>
              <Icon name='car-front' className={styles.textSummaryIcon} />
              <Label>
                {licensePlate.length > 12 ? (
                  <HintPopup title={licensePlate} placement='top' arrow>
                    <div>{`${licensePlate.slice(0, 12)}...`}</div>
                  </HintPopup>
                ) : (
                  licensePlate
                )}
              </Label>
              <CaptionDefault>
                {modelName.length > 13 ? (
                  <HintPopup title={modelName} placement='top' arrow>
                    <div>{`${modelName.slice(0, 13)}...`}</div>
                  </HintPopup>
                ) : (
                  modelName
                )}
              </CaptionDefault>
            </div>
          </div>

          <Divider />

          <div className={styles.contentSection}>
            <div>
              <DescriptionTitle>Informações do vídeo</DescriptionTitle>
            </div>

            <div>
              <Icon name='video' className={styles.textSummaryIcon} />
              <Label>{mediaTypeLabel}</Label>
              <CaptionDefault>{mediaType}</CaptionDefault>
            </div>

            <div>
              <Icon name='calendar-days' className={styles.textSummaryIcon} />
              <Label>{requestedDay}</Label>
              <CaptionDefault>{requestPeriod}</CaptionDefault>
            </div>
          </div>
        </div>

        <Checkbox
          className={styles.checkbox}
          checked={mediaRequestData?.hasNotificationEnabled}
          onClick={onClickEmailSendConfirmation}
          label='Avise-me por e-mail quando o vídeo estiver disponível'
        />
      </div>
      <div className={styles.rightSideWrapper}>
        <MovieFilterIcon />
      </div>
    </div>
  );
});

export default ConfirmationStep;
